import type { KeyMessageGroup } from "@/store/news-feed/key-messages/key-message.slice";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";
import { useAnalysisSidebarFilterParams } from "@/store/news-feed/params/analysis-params.context";
import { useCallback, useEffect, useMemo, useState } from "react";

const ITEMS_TO_SHOW = 8;

/**
 * Utility to get updated selected groups
 */
const getUpdatedSelectedGroups = (
  groupId: string,
  shouldSelect: boolean,
  selectedGroups: Set<string>,
): Set<string> => {
  if (!groupId) {
    console.warn("Attempted to update groups with invalid groupId");
    return selectedGroups;
  }
  return shouldSelect
    ? new Set([...selectedGroups, groupId])
    : new Set([...selectedGroups].filter((id) => id !== groupId));
};

/**
 * Hook to manage the state and operations for grouped key message filters
 * @param searchTerm - Current search term for filtering groups and items
 * @param selectedGroups - Set of currently selected group IDs
 */
export const useKeyMessageFilterState = (
  searchTerm: string,
  selectedGroups: Set<string>,
) => {
  const keyMessageGroups = useKeyMessageStore(
    (state) => state.keyMessageGroups,
  );
  const setSelectedGroups = useKeyMessageStore(
    (state) => state.setSelectedGroups,
  );
  const { toggleFilterSelection, setCategorySelection, selectedFilters } =
    useAnalysisSidebarFilterParams();
  const selectedKeyMessages = selectedFilters.get("KEY_MESSAGE") ?? [];

  // Initialize expandedGroups with the first group's ID if available
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Effect to expand first group only on mount and when groups change
  useEffect(() => {
    const firstGroup = keyMessageGroups[0];
    if (firstGroup) {
      setExpandedGroups((prev) => {
        // If the first group is already expanded, keep it that way
        if (prev.has(firstGroup.id)) {
          return prev;
        }
        // Otherwise, create a new set with the first group expanded
        return new Set([firstGroup.id]);
      });
    }
  }, [keyMessageGroups]);

  // Effect to sync group selection state with actual selected items
  // This ensures groups are unchecked when all their items are deselected
  useEffect(() => {
    const currentSelectedSet = new Set(
      selectedKeyMessages.map((id) => String(id)),
    );

    // Check each group and update its selection state
    for (const group of keyMessageGroups) {
      const groupItemIds = group.keyMessageItems.map((item) => String(item.id));
      const isGroupFullySelected =
        groupItemIds.length > 0 &&
        groupItemIds.every((id) => currentSelectedSet.has(id));

      const isCurrentlySelected = selectedGroups.has(group.id);

      // Only update if state doesn't match
      if (isGroupFullySelected !== isCurrentlySelected) {
        setSelectedGroups(
          getUpdatedSelectedGroups(
            group.id,
            isGroupFullySelected,
            selectedGroups,
          ),
        );
      }
    }
  }, [
    selectedKeyMessages,
    keyMessageGroups,
    selectedGroups,
    setSelectedGroups,
  ]);

  const toggleGroupSelection = useCallback(
    (group: KeyMessageGroup): void => {
      const itemIds = group.keyMessageItems.map((item) => String(item.id));
      const isGroupSelected = selectedGroups.has(group.id);
      const selectedSet = new Set(selectedKeyMessages);

      if (setCategorySelection) {
        // Analysis route: one batch update (toggleFilterSelection in a loop would use stale URL state)
        if (isGroupSelected) {
          const next = selectedKeyMessages.filter(
            (id) => !itemIds.includes(id),
          );
          setCategorySelection("KEY_MESSAGE", next);
        } else {
          const combined = new Set([...selectedKeyMessages, ...itemIds]);
          setCategorySelection("KEY_MESSAGE", Array.from(combined));
        }
      } else {
        // Old news feed: store updates sync, so loop is fine
        if (isGroupSelected) {
          for (const id of itemIds) {
            if (selectedSet.has(id)) {
              toggleFilterSelection("KEY_MESSAGE", id);
            }
          }
        } else {
          for (const id of itemIds) {
            if (!selectedSet.has(id)) {
              toggleFilterSelection("KEY_MESSAGE", id);
            }
          }
        }
      }

      // Update group selection state
      setSelectedGroups(
        getUpdatedSelectedGroups(group.id, !isGroupSelected, selectedGroups),
      );
    },
    [
      selectedGroups,
      setSelectedGroups,
      toggleFilterSelection,
      setCategorySelection,
      selectedKeyMessages,
    ],
  );

  const toggleItemSelection = useCallback(
    (itemId: string, group: KeyMessageGroup): void => {
      // Get current state before toggling
      const currentSelected = selectedKeyMessages;
      const itemIdStr = String(itemId);
      const wasSelected = currentSelected.includes(itemIdStr);

      // Toggle the item
      toggleFilterSelection("KEY_MESSAGE", itemId);

      // Calculate what the new state will be after toggling
      const newSelectedSet = new Set(currentSelected.map((id) => String(id)));
      if (wasSelected) {
        newSelectedSet.delete(itemIdStr);
      } else {
        newSelectedSet.add(itemIdStr);
      }

      // Check if all items in the group are selected after this toggle
      const groupItemIds = group.keyMessageItems.map((item) => String(item.id));
      const isGroupFullySelected =
        groupItemIds.length > 0 &&
        groupItemIds.every((id) => newSelectedSet.has(id));

      // Update group selection state
      // If group is not fully selected (including when all items are deselected),
      // ensure it's unchecked
      setSelectedGroups(
        getUpdatedSelectedGroups(
          group.id,
          isGroupFullySelected,
          selectedGroups,
        ),
      );
    },
    [
      selectedGroups,
      setSelectedGroups,
      toggleFilterSelection,
      selectedKeyMessages,
    ],
  );

  const filteredGroups = useMemo<KeyMessageGroup[]>(() => {
    return keyMessageGroups
      .map((group) => {
        const filteredItems = group.keyMessageItems
          .filter(
            (item) =>
              !searchTerm ||
              item.name.toLowerCase().includes(searchTerm.toLowerCase()),
          )
          .sort((a, b) => {
            // Sort by count (total) in descending order
            const countA = a.total ?? 0;
            const countB = b.total ?? 0;
            return countB - countA;
          });

        return { ...group, keyMessageItems: filteredItems };
      })
      .filter((group) => {
        // Show all groups when there's no search term
        if (!searchTerm) return true;
        // Only filter out empty groups when searching
        return group.keyMessageItems.length > 0;
      });
  }, [keyMessageGroups, searchTerm]);

  const toggleExpandGroups = (groupId: string, isExpanded: boolean) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(groupId);
      } else {
        newSet.delete(groupId);
      }
      return newSet;
    });
  };

  return {
    toggleGroupSelection,
    toggleItemSelection,
    filteredGroups,
    toggleExpandGroups,
    expandedGroups,
  };
};

export const useKeyMessageExpandablePagination = <T>(
  items: T[],
  groupId: string,
) => {
  const [visibleCount, setVisibleCount] = useState<number>(ITEMS_TO_SHOW);

  useEffect(() => {
    // Reset when group changes
    setVisibleCount(ITEMS_TO_SHOW);
  }, [groupId]);

  const hasMoreToShow = visibleCount < items.length;
  const canShowLess = visibleCount > ITEMS_TO_SHOW;

  const showMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + ITEMS_TO_SHOW, items.length));
  }, [items.length]);

  const showLess = useCallback(() => {
    setVisibleCount(ITEMS_TO_SHOW);
  }, []);

  return {
    showMore,
    showLess,
    hasMoreToShow,
    canShowLess,
    visibleCount,
  };
};
